import React, { useState } from "react";
import { motion } from "framer-motion";
import { Target, Trophy, ArrowLeft } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { QuizEngine } from "@/components/QuizEngine";
import { Button } from "@/components/ui/button";
import { getLesson } from "@/data/curriculum";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// A mixed challenge quiz built from a few questions across lessons.
const MIXED = [
  ...getLesson(2).quiz.slice(0, 2),
  ...getLesson(3).quiz.slice(0, 1),
  ...getLesson(4).quiz.slice(0, 1),
  ...getLesson(13).quiz.slice(0, 1),
  ...getLesson(14).quiz.slice(1, 2),
].map((q, i) => ({ ...q, id: `mix-${i}` }));

export default function Quizzes() {
  const { user, progress, saveQuiz } = useAuth();
  const [active, setActive] = useState(null); // {questions, id, title}
  const scores = progress?.quiz_scores || {};

  const start = (id, questions, title) => setActive({ id, questions, title });

  const onComplete = async (score, total) => {
    if (active && user) {
      await saveQuiz(active.id, score, total);
      toast.success(`Saved: ${score}/${total} on ${active.title}`);
    }
  };

  if (active) {
    return (
      <div className="min-h-screen text-foreground">
        <Navbar />
        <div className="mx-auto max-w-2xl px-4 sm:px-8 py-10 lg:py-14">
          <button onClick={() => setActive(null)} data-testid="quiz-back" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary">
            <ArrowLeft size={15} /> All quizzes
          </button>
          <h1 className="mt-5 mb-6 font-serif text-3xl">{active.title}</h1>
          <QuizEngine questions={active.questions} quizId={active.id} title={active.title} onComplete={onComplete} />
        </div>
      </div>
    );
  }

  const cards = [
    { id: "mix-challenge", title: "Mixed Challenge", desc: "Six questions drawn from across both courses.", questions: MIXED },
    { id: "lesson-2", title: "Treble Clef Drill", desc: "Name notes and spaces on the treble staff.", questions: getLesson(2).quiz },
    { id: "lesson-3", title: "Bass Clef Drill", desc: "Master the lower staff — lines and spaces.", questions: getLesson(3).quiz },
    { id: "lesson-4", title: "Rhythm & Duration", desc: "Whole, half, quarter and eighth note values.", questions: getLesson(4).quiz },
    { id: "lesson-8", title: "Solfege & Sight-Singing", desc: "Do-Re-Mi and the major scale degrees.", questions: getLesson(8).quiz },
    { id: "lesson-14", title: "Triads & Chords", desc: "Advanced: major, minor, diminished & augmented.", questions: getLesson(14).quiz },
    { id: "lesson-17", title: "The Church Modes", desc: "Advanced: Dorian, Phrygian, Lydian & more.", questions: getLesson(17).quiz },
  ];

  return (
    <div className="min-h-screen text-foreground">
      <Navbar />
      <div className="mx-auto max-w-7xl px-4 sm:px-8 py-10 lg:py-16">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">Test yourself</p>
        <h1 className="mt-3 font-serif text-4xl tracking-tight sm:text-5xl">Quizzes</h1>
        <p className="mt-4 max-w-xl text-muted-foreground">
          Sharpen your reading with focused drills. {user ? "Your best score is saved for each quiz." : "Log in to save your scores."}
        </p>

        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {cards.map((c, i) => {
            const best = scores[c.id]?.best;
            return (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: (i % 3) * 0.06 }}
                className="flex flex-col rounded-2xl border border-border bg-card p-6"
              >
                <div className="flex items-center justify-between">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <Target size={20} />
                  </span>
                  {best != null && (
                    <span className="flex items-center gap-1 rounded-full bg-secondary px-3 py-1 text-xs font-medium text-primary">
                      <Trophy size={12} /> Best {best}/{scores[c.id].total}
                    </span>
                  )}
                </div>
                <h3 className="mt-4 font-serif text-xl">{c.title}</h3>
                <p className="mt-2 flex-1 text-sm text-muted-foreground">{c.desc}</p>
                <Button
                  onClick={() => start(c.id, c.questions, c.title)}
                  data-testid={`start-quiz-${c.id}`}
                  className="mt-5 w-full rounded-full bg-primary text-foreground hover:bg-primary/90"
                >
                  {best != null ? "Play again" : "Start quiz"}
                </Button>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
